import React, { useState, useCallback, useEffect } from 'react';
import type { UseFingeringEditorReturn } from './useFingeringEditor';
import { useSyncedRef } from './useSyncedRef';
import { ClipboardData, ClipboardItem, buildClipboardItems, buildPastedBadges } from '../utils/fingeringClipboard';

export interface UseFingeringClipboardReturn {
  hasClipboard: boolean;
  copySelection: () => void;
  placementActive: boolean;
  startPlacement: () => void;
  cancelPlacement: () => void;
  commitPasteAt: (svgX: number, svgY: number) => void;
  ghostPos: { x: number; y: number } | null;
  ghostItems: ClipboardItem[];
  handlePlacementPointerMove: (e: React.PointerEvent) => void;
  handlePlacementPointerDown: (e: React.PointerEvent) => void;
  handlePlacementPointerUp: (e: React.PointerEvent) => void;
  handlePlacementPointerCancel: (e: React.PointerEvent) => void;
}

export function useFingeringClipboard(editor: UseFingeringEditorReturn): UseFingeringClipboardReturn {
  const [clipboard, setClipboard] = useState<ClipboardData>({ items: [] });
  const [placementActive, setPlacementActive] = useState(false);
  const [ghostPos, setGhostPos] = useState<{ x: number; y: number } | null>(null);
  const hasClipboard = clipboard.items.length > 0;

  const copySelection = useCallback(() => {
    const data = buildClipboardItems(editor.data.badges, editor.mode.selectedIds);
    if (data.items.length === 0) return;
    setClipboard(data);
    editor.handlers.showToast('コピーしました');
  }, [editor]);

  const startPlacement = useCallback(() => {
    if (!hasClipboard) return;
    setPlacementActive(true);
  }, [hasClipboard]);

  const cancelPlacement = useCallback(() => {
    setPlacementActive(false);
    setGhostPos(null);
  }, []);

  const commitPasteAt = useCallback((svgX: number, svgY: number) => {
    if (clipboard.items.length === 0) return;
    // undo 1 単位: ペースト前に history を積む
    editor.handlers.pushHistory();
    const pasted = buildPastedBadges(
      clipboard,
      svgX,
      svgY,
      editor.view.currentPage,
      () => editor.refs.nextIdRef.current++,
      () => editor.refs.nextBeatIndexRef.current++,
    );
    editor.data.setBadges((prev) => [...prev, ...pasted]);
    // 貼った塊を選択維持（selectRange は渡した id を必ず選択集合に入れる）
    editor.handlers.selectRange(pasted.map((b) => b.id));
    // 配置後の合成 click（handleSvgClick の dismissPanel）で選択が消えるのを抑止
    editor.refs.clickSuppressRef.current = true;
    if (editor.view.isMobile) editor.mode.setEditorMode('move');
    setPlacementActive(false);
    setGhostPos(null);
    editor.handlers.showToast('運指を貼り付けました');
  }, [clipboard, editor]);

  // 配置モードのポインタは常に既定動作 (scroll 等) を殺して SVG 座標へ変換する。
  // preventDefault + stopPropagation + clientToSvg の共通前処理を 1 箇所に集約する。
  const suppressAndLocate = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    return editor.handlers.clientToSvg(e.clientX, e.clientY);
  }, [editor]);

  const handlePlacementPointerMove = useCallback((e: React.PointerEvent) => {
    const p = suppressAndLocate(e);
    if (p) setGhostPos(p);
  }, [suppressAndLocate]);

  const handlePlacementPointerDown = useCallback((e: React.PointerEvent) => {
    const p = suppressAndLocate(e);
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* jsdom 等を許容 */ }
    if (p) setGhostPos(p);
  }, [suppressAndLocate]);

  const handlePlacementPointerUp = useCallback((e: React.PointerEvent) => {
    const p = suppressAndLocate(e) ?? ghostPos;
    if (p) commitPasteAt(p.x, p.y);
  }, [suppressAndLocate, ghostPos, commitPasteAt]);

  const handlePlacementPointerCancel = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // 中断（システム割込み等）: 確定せず ghost だけ消す。placement は継続しリトライ可能。
    setGhostPos(null);
  }, []);

  // 最新値を ref に同期して listener の再登録を避ける。
  const copyRef = useSyncedRef(copySelection);
  const startRef = useSyncedRef(startPlacement);
  const cancelRef = useSyncedRef(cancelPlacement);
  const placementActiveRef = useSyncedRef(placementActive);
  const hasClipboardRef = useSyncedRef(hasClipboard);
  const hasSelectionRef = useSyncedRef(editor.mode.selectedIds.size > 0);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || (t?.isContentEditable ?? false);
      if (isEditable) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === 'c' || e.key === 'C')) {
        if (!hasSelectionRef.current) return;
        e.preventDefault();
        copyRef.current();
        return;
      }
      if (mod && (e.key === 'v' || e.key === 'V')) {
        if (!hasClipboardRef.current) return;
        e.preventDefault();
        startRef.current();
        return;
      }
      if (e.key === 'Escape' && placementActiveRef.current) {
        // 配置モード中の Esc だけ握る。Desktop hook の Esc(選択解除, bubble フェーズ)より
        // 先に走る capture フェーズで stopImmediatePropagation して選択維持を守る。
        e.preventDefault();
        e.stopImmediatePropagation();
        cancelRef.current();
      }
    };
    // capture=true: Desktop hook の bubble listener より前に評価する。
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [copyRef, startRef, cancelRef, placementActiveRef, hasClipboardRef, hasSelectionRef]);

  return {
    hasClipboard,
    copySelection,
    placementActive,
    startPlacement,
    cancelPlacement,
    commitPasteAt,
    ghostPos,
    ghostItems: clipboard.items,
    handlePlacementPointerMove,
    handlePlacementPointerDown,
    handlePlacementPointerUp,
    handlePlacementPointerCancel,
  };
}
